<template>
    <v-container fluid class="ma-0 pa-0">
        <v-row class="ma-0 pa-0">
            <v-col cols="12" md="6">
                <v-card class="elevation-2">
                    <v-card-title>Top 10 Clienti per Fatturato</v-card-title>
                    <v-card-text>
                        <doughnut-chart :data="graficoTopClienti" :options="opzioniTorta" :key="keyTop" />
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" md="6">
                <v-card class="elevation-2">
                    <v-card-title>
                        Distribuzione ABC Clienti
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-icon small class="ml-2" v-on="on">info</v-icon>
                            </template>
                            <span>Classe A: 80% fatturato | Classe B: 15% | Classe C: 5%</span>
                        </v-tooltip>
                    </v-card-title>
                    <v-card-text>
                        <div class="mb-4">
                            <div class="d-flex align-center mb-2">
                                <v-chip color="success" small class="mr-2">A</v-chip>
                                <div class="flex-grow-1">
                                    <v-progress-linear :value="analisiABC.a.percentuale" color="success" height="25">
                                        <strong>{{ analisiABC.a.numero }} clienti - {{
                                            analisiABC.a.percentuale.toFixed(1) }}%</strong>
                                    </v-progress-linear>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-2">
                                <v-chip color="warning" small class="mr-2">B</v-chip>
                                <div class="flex-grow-1">
                                    <v-progress-linear :value="analisiABC.b.percentuale" color="warning" height="25">
                                        <strong>{{ analisiABC.b.numero }} clienti - {{
                                            analisiABC.b.percentuale.toFixed(1) }}%</strong>
                                    </v-progress-linear>
                                </div>
                            </div>
                            <div class="d-flex align-center">
                                <v-chip color="error" small class="mr-2">C</v-chip>
                                <div class="flex-grow-1">
                                    <v-progress-linear :value="analisiABC.c.percentuale" color="error" height="25">
                                        <strong>{{ analisiABC.c.numero }} clienti - {{
                                            analisiABC.c.percentuale.toFixed(1) }}%</strong>
                                    </v-progress-linear>
                                </div>
                            </div>
                        </div>

                        <v-data-table :headers="intestazioniABC" :items="tabellaABC" height="300px" dense fixed-header
                            disable-pagination hide-default-footer class="elevation-1" mobile-breakpoint="0">
                            <template v-slot:item.classe="{ item }">
                                <v-chip small :color="getColoreABC(item.classe)">{{ item.classe }}</v-chip>
                            </template>
                            <template v-slot:item.fatturato="{ item }">
                                € {{ $functions.numberWithCommas(item.fatturato.toFixed(2)) }}
                            </template>
                            <template v-slot:item.percentuale="{ item }">
                                {{ item.percentuale.toFixed(1) }}%
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row class="ma-0 pa-0 mt-3">
            <v-col cols="12">
                <v-card class="elevation-2">
                    <v-card-title>
                        Matrice Fatturato Mensile per Cliente
                        <v-spacer />
                        <v-text-field v-model="ricercaHeatmap" append-icon="search" label="Cerca cliente" single-line
                            hide-details dense style="max-width: 300px;" />
                    </v-card-title>
                    <v-card-text class="pl-0 pr-0">
                        <div class="heatmap-container" style="overflow-x: auto;">
                            <table class="heatmap-table">
                                <thead>
                                    <tr>
                                        <th class="text-left sticky-col">Cliente</th>
                                        <th v-for="m in 12" :key="m">{{ getNomeMese(m - 1) }}</th>
                                        <th class="font-weight-bold">Totale</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="cliente in heatmapFiltrato" :key="cliente.codice">
                                        <td class="font-weight-bold sticky-col">{{ cliente.nome }}</td>
                                        <td v-for="(val, i) in cliente.mesi" :key="i"
                                            :style="{ backgroundColor: getColoreHeat(val, cliente.max) }"
                                            class="text-center">
                                            {{ val > 0 ? formattaValuta(val) : '-' }}
                                        </td>
                                        <td class="font-weight-bold text-right">{{ formattaValuta(cliente.totale) }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { get_dati_grafico_tot, get_data } from "@/api/api.documenti.js";
import DoughnutChart from "@/components/chart/DoughnutChart.js";
import mixinCharts from "@/components/chart/mixinCharts.js";

export default {
    name: "TabComposizione",

    components: {
        DoughnutChart
    },

    mixins: [mixinCharts],

    props: {
        filtri: { type: Object, default: () => ({}) },
        aggiornaDati: { type: Number, default: 0 }
    },

    data: () => ({
        graficoTopClienti: {},
        opzioniTorta: {},
        keyTop: 0,

        analisiABC: {
            a: { numero: 0, percentuale: 0, fatturato: 0 },
            b: { numero: 0, percentuale: 0, fatturato: 0 },
            c: { numero: 0, percentuale: 0, fatturato: 0 }
        },

        intestazioniABC: [
            { text: 'Cliente', value: 'nome' },
            { text: 'Classe', value: 'classe', align: 'center' },
            { text: 'Fatturato', value: 'fatturato', align: 'end' },
            { text: '% sul totale', value: 'percentuale', align: 'end' }
        ],

        tabellaABC: [],
        datiHeatmap: [],
        ricercaHeatmap: ''
    }),

    computed: {
        heatmapFiltrato() {
            if (!this.ricercaHeatmap) return this.datiHeatmap;

            const cerca = this.ricercaHeatmap.toLowerCase();
            return this.datiHeatmap.filter(c =>
                c.nome.toLowerCase().includes(cerca)
            );
        }
    },

    methods: {
        async caricaDatiTopClienti() {
            try {
                let res = await get_dati_grafico_tot(this.filtri);
                this.graficoTopClienti = res.data;
                this.keyTop++;

            } catch (error) {
                console.error("Errore caricamento top clienti:", error);
            }
        },

        async caricaDatiABC() {
            try {
                let res = await get_data("fatture/analisi-abc", this.filtri);

                if (res && res.data) {
                    const dati = res.data;

                    // Calcola totali per percentuali
                    const totale = dati.clienti.reduce((acc, c) => acc + c.fatturato, 0);

                    // Ordina per fatturato decrescente
                    const clientiOrdinati = dati.clienti.sort((a, b) => b.fatturato - a.fatturato);

                    // Calcola classi ABC
                    let sommaA = 0, sommaB = 0, sommaC = 0;
                    let numeroA = 0, numeroB = 0, numeroC = 0;
                    let cumulato = 0;

                    this.tabellaABC = clientiOrdinati.map(cliente => {
                        cumulato += cliente.fatturato;
                        const percCumulata = (cumulato / totale) * 100;

                        let classe = 'C';
                        if (percCumulata <= 80) {
                            classe = 'A';
                            sommaA += cliente.fatturato;
                            numeroA++;
                        } else if (percCumulata <= 95) {
                            classe = 'B';
                            sommaB += cliente.fatturato;
                            numeroB++;
                        } else {
                            sommaC += cliente.fatturato;
                            numeroC++;
                        }

                        return {
                            nome: cliente.ragSoc,
                            classe: classe,
                            fatturato: cliente.fatturato,
                            percentuale: (cliente.fatturato / totale) * 100
                        };
                    });

                    this.analisiABC.a = {
                        numero: numeroA,
                        percentuale: (sommaA / totale) * 100,
                        fatturato: sommaA
                    };

                    this.analisiABC.b = {
                        numero: numeroB,
                        percentuale: (sommaB / totale) * 100,
                        fatturato: sommaB
                    };

                    this.analisiABC.c = {
                        numero: numeroC,
                        percentuale: (sommaC / totale) * 100,
                        fatturato: sommaC
                    };
                }

            } catch (error) {
                console.error("Errore caricamento analisi ABC:", error);
            }
        },

        async caricaDatiHeatmap() {
            try {
                let res = await get_data("fatture/heatmap-clienti", this.filtri);

                if (res && res.data) {
                    this.datiHeatmap = res.data.map(cliente => {
                        const totale = cliente.mesi.reduce((a, b) => a + b, 0);
                        const max = Math.max(...cliente.mesi);

                        return {
                            codice: cliente.codAnagrafica,
                            nome: cliente.ragSoc,
                            mesi: cliente.mesi,
                            totale: totale,
                            max: max
                        };
                    });

                    // Ordina per totale decrescente
                    this.datiHeatmap.sort((a, b) => b.totale - a.totale);

                    // Limita ai top 20 clienti per leggibilità
                    this.datiHeatmap = this.datiHeatmap.slice(0, 20);
                }

            } catch (error) {
                console.error("Errore caricamento heatmap:", error);
            }
        },

        getColoreABC(classe) {
            return classe === 'A' ? 'success' : classe === 'B' ? 'warning' : 'error';
        },

        getNomeMese(indice) {
            const mesi = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
            return mesi[indice];
        },

        getColoreHeat(valore, max) {
            if (valore === 0) return '#FFFFF';
            const intensita = valore / max;
            const r = Math.floor(76 + (255 - 76) * (1 - intensita));
            const g = Math.floor(175 + (255 - 175) * (1 - intensita));
            const b = Math.floor(80 + (255 - 80) * (1 - intensita));
            return `rgba(${r}, ${g}, ${b}, 0.7)`;
        },

        formattaValuta(valore) {
            return '€' + this.$functions.numberWithCommas(valore.toFixed(0));
        },

        caricaTuttiDati() {
            this.caricaDatiTopClienti();
            this.caricaDatiABC();
            this.caricaDatiHeatmap();
        }
    },

    mounted() {
        this.opzioniTorta = this.getOptionsDoughnutChart();
        this.caricaTuttiDati();
    },

    watch: {
        aggiornaDati() {
            this.caricaTuttiDati();
        }
    }
}
</script>